import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { axios } from "@/client/libs/axios";
import { queryKeys } from "@/client/queries/keys";

type HealthResponse = {
  status: "ok" | "degraded" | "down" | string;
};

export type SystemStatus = "operational" | "degraded" | "down" | "unknown";

type UseStatusOptions = {
  /**
   * If true (default), the first /api/health request is deferred with
   * `requestIdleCallback` so it doesn't compete with critical first-paint
   * resources. Pass `false` for surfaces that need the status immediately
   * (e.g. an admin dashboard) — but the marketing footer should always
   * defer (perf audit item #7).
   */
  deferUntilIdle?: boolean;
};

const noop = () => {
  // intentionally empty — used as a no-op cancel for SSR / unsupported envs.
};

const idleDelay = (callback: () => void): (() => void) => {
  if (typeof window === "undefined") return noop;

  // `requestIdleCallback` exists in Chromium / Firefox / Safari 17.4+.
  // Cast through `unknown` because the type is missing from older lib.dom.d.ts
  // releases; we feature-detect at runtime so the cast is safe.
  type IdleHandle = number;
  type IdleCallbacks = {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => IdleHandle;
    cancelIdleCallback?: (handle: IdleHandle) => void;
  };
  const w = window as unknown as Window & IdleCallbacks;

  if (typeof w.requestIdleCallback === "function") {
    const handle = w.requestIdleCallback(callback, { timeout: 3000 });
    return () => {
      if (typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(handle);
    };
  }

  // Fallback for older browsers — 2 s timeout is well past typical LCP,
  // so we still don't compete with critical first-paint work.
  const handle = window.setTimeout(callback, 2000);
  return () => {
    window.clearTimeout(handle);
  };
};

/**
 * useStatus — polls /api/health every 60 seconds to determine system status.
 * Polling pauses when the browser tab is hidden.
 *
 * The first request is deferred until the browser is idle by default; this
 * keeps the marketing footer's status pill out of the critical first-paint
 * path (perf audit item #7). Pass `{ deferUntilIdle: false }` if you need
 * the status synchronously on mount.
 *
 * Status mapping:
 *   { status: "ok" }       → "operational"
 *   { status: "degraded" } → "degraded"
 *   non-2xx HTTP           → "down"
 *   network error/timeout  → "unknown"
 */
export const useStatus = ({ deferUntilIdle = true }: UseStatusOptions = {}): {
  status: SystemStatus;
} => {
  const [enabled, setEnabled] = useState(!deferUntilIdle);

  useEffect(() => {
    if (!deferUntilIdle || enabled) return;
    return idleDelay(() => {
      setEnabled(true);
    });
  }, [deferUntilIdle, enabled]);

  const { data, isError, isPending } = useQuery({
    queryKey: queryKeys.system.health(),
    queryFn: async (): Promise<HealthResponse> => {
      const response = await axios.get<HealthResponse>("/health");
      return response.data;
    },
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
  });

  if (!enabled || isPending) return { status: "unknown" };
  if (isError) return { status: "unknown" };

  const apiStatus = data.status;

  if (apiStatus === "ok") return { status: "operational" };
  if (apiStatus === "degraded") return { status: "degraded" };

  return { status: "down" };
};
